import React, { useState, useEffect } from "react";
import {
  getApplications,
  getLicenses,
  createLicense,
  createOrganisation,
  unbindDevice,
  updateLicenseStatus,
  markLicenseInactive,
  extendLicense,
  fetchWithAuth,
} from "../Services/api";
import Alert from "../Components/Alert";
import ConfirmDialog from "../Components/ConfirmDialog";
import InputPromptDialog from "../Components/InputPromptDialog";
import LicenseFormModal from "../Components/LicenseFormModal";
import LicenseDetailsModal from "../Components/LicenseDetailsModal";
import ExtendLicenseModal from "../Components/ExtendLicenseModal";
import MessageModal from "../Components/MessageModal";
import LicenseRequestsPage from "./LicenseRequestsPage";
import CustomerDetailPage from "./CustomerDetailPage";
import { formatLicenseDeviceInfo, formatLicenseExpiry, formatLicenseActivationStatus } from "../utils/deviceInfo";
import {
  groupLicensesForDisplay,
  matchesLicenseFilter,
  matchesLicenseSearch,
} from "../utils/licenseListGrouping";
import "./Dashboard.css";

const Dashboard = ({ user, onLogout }) => {
  const [currentPage, setCurrentPage] = useState("licenses");
  const [licenses, setLicenses] = useState([]);
  const [applicationData, setApplicationData] = useState([]);
  const [filteredLicenses, setFilteredLicenses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [alert, setAlert] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [inputPrompt, setInputPrompt] = useState(null);
  const [modalMessage, setModalMessage] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    expired: 0,
  });
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [customerView, setCustomerView] = useState(null);

  const showAlert = (message, type = "info") => setAlert({ message, type });
  const closeAlert = () => setAlert(null);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth("/license-history");
      const data = response.data || [];
      setHistory(data);
      setFilteredHistory(data);
    } catch (err) {
      console.error("Error loading history:", err);
      showAlert("Failed to load history", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFilteredHistory(
      history.filter(
        (entry) =>
          (entry.client_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (entry.license_key || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (entry.action || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (entry.device_id || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (entry.performed_by || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, history]);

  const showConfirm = (message, onConfirm, type = "warning") => {
    if (type === "reject") {
      setInputPrompt({
        message,
        onConfirm,
        placeholder: "Enter rejection reason...",
      });
    } else if (type === "delete") {
      setConfirmDialog({
        message,
        onConfirm,
        type: "danger",
        confirmText: "Delete",
        cancelText: "Cancel",
      });
    } else {
      setConfirmDialog({
        message,
        onConfirm,
        type,
        confirmText: "Confirm",
        cancelText: "Cancel",
      });
    }
  };

  const closeConfirm = () => {
    setConfirmDialog(null);
    setInputPrompt(null);
  };

  const loadApplications = async () => {
    try {
      const data = await getApplications();
      setApplicationData(data);
    } catch (err) {
      showAlert("Failed to load applications", "error");
    }
  };

  useEffect(() => {
    loadApplications();
  }, []);

  useEffect(() => {
    if (currentPage === "licenses") {
      loadLicenses(true);
    } else if (currentPage === "history") {
      loadHistory();
    }
  }, [currentPage]);

  const loadLicenses = async (showLoadingIndicator = true) => {
    if (showLoadingIndicator) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await getLicenses();
      setLicenses(data);
      setFilteredLicenses(data);
      setStats({
        total: data.length,
        active: data.filter((l) => l.status === "active" && l.device_id).length,
        pending: data.filter((l) => !l.device_id).length,
        expired: data.filter(
          (l) =>
            l.device_id &&
            (l.status === "inactive" || l.status === "expired")
        ).length,
      });
    } catch (err) {
      showAlert("Failed to load licenses", "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const grouped = groupLicensesForDisplay(licenses);
    let filtered = grouped.filter((row) => matchesLicenseFilter(row, activeFilter));
    if (searchQuery) {
      filtered = filtered.filter((row) => matchesLicenseSearch(row, searchQuery));
    }
    setFilteredLicenses(filtered);
  }, [searchQuery, activeFilter, licenses]);

  const handleCreateLicense = async (formData) => {
    try {
      if (formData.customerType === 'organisation') {
        const result = await createOrganisation(formData);
        setShowForm(false);
        const keys = (result.licenses || []).map((l) => `${l.system_name}: ${l.license_key}`).join('\n');
        setModalMessage(
          `Organisation created!\n\n${result.org?.org_name}\n\nLicenses:\n${keys}`
        );
      } else {
        const newLicense = await createLicense(formData);
        setShowForm(false);
        setModalMessage(
          `License created!\n\nClient : ${formData.clientName}\nEmail : ${formData.email}\nLicense Key : ${newLicense.license_key}\n\nEmail this key to the client. It will bind to their machine on first activation.`
        );
      }
      showAlert("License Generated Successfully!", "success");
      loadLicenses(true);
    } catch (err) {
      showAlert(`Failed: ${err.message}`, "error");
    }
  };

  const openCustomerView = (row) => {
    if (row._rowType === "organisation" || (row.customer_type === "organisation" && row.organisation_id)) {
      setCustomerView({ id: row.organisation_id, type: "organisation" });
      return;
    }
    setCustomerView({ id: row.id, type: "individual" });
  };

  const handleUnbind = (licenseId) => {
    showConfirm(
      "Transfer this license to a new machine? This removes the current device binding.",
      async () => {
        try {
          await unbindDevice(licenseId);
          showAlert(
            "Device binding removed — license can be activated on a new machine",
            "success"
          );
          loadLicenses(true);
        } catch (err) {
          showAlert("Failed to transfer license", "error");
        }
        closeConfirm();
      }
    );
  };

  const handleMarkInactive = (licenseId) => {
    showConfirm(
      "Mark license INACTIVE?",
      async () => {
        try {
          await markLicenseInactive(licenseId);
          showAlert("License marked inactive", "success");
          loadLicenses(true);
        } catch (err) {
          showAlert("Failed", "error");
        }
        closeConfirm();
      },
      "danger"
    );
  };

  const handleExtendLicense = async (licenseId, days) => {
    try {
      await extendLicense(licenseId, days);
      showAlert(`Extended by ${days} days`, "success");
      setShowExtendModal(null);
      loadLicenses(true);
    } catch (err) {
      showAlert("Failed to extend", "error");
    }
  };

  const handleUpdateStatus = (licenseId, newStatus) => {
    showConfirm("Update status?", async () => {
      try {
        await updateLicenseStatus(licenseId, newStatus);
        showAlert("Status updated", "success");
        loadLicenses(true);
      } catch (err) {
        showAlert("Failed", "error");
      }
      closeConfirm();
    });
  };

  const getStatusInfo = (license) => {
    if (license.status === "active" && license.device_id)
      return { text: "Active", class: "status-active" };
    if (!license.device_id)
      return { text: "Pending", class: "status-pending" };
    if (license.status === "inactive")
      return { text: "Inactive", class: "status-inactive" };
    return { text: "Expired", class: "status-expired" };
  };

  return (
    <div className="dashboard-container">
      {alert && (
        <Alert message={alert.message} type={alert.type} onClose={closeAlert} />
      )}
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          type={confirmDialog.type}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
          onCancel={closeConfirm}
          onConfirm={async () => {
            await confirmDialog.onConfirm();
            closeConfirm();
          }}
        />
      )}
      {inputPrompt && (
        <InputPromptDialog
          {...inputPrompt}
          onConfirm={(v) => {
            inputPrompt.onConfirm(v);
            closeConfirm();
          }}
          onCancel={closeConfirm}
        />
      )}

      <div className="dashboard-header-card">
        <div className="dashboard-header">
          <div className="header-info">
            <h1>License Management System</h1>
            <p>Admin Dashboard</p>
          </div>
          <div className="header-user">
            <div className="user-info">
              <div className="user-name">{user.username}</div>
              <div className="user-role">{user.role}</div>
            </div>
            <button onClick={onLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
        <div className="dashboard-tabs">
          <button
            onClick={() => setCurrentPage("licenses")}
            className={`tab-btn ${currentPage === "licenses" ? "active" : ""}`}>
            Licenses
          </button>
          <button
            onClick={() => setCurrentPage("requests")}
            className={`tab-btn ${currentPage === "requests" ? "active" : ""}`}>
            License Requests
          </button>
          <button
            onClick={() => setCurrentPage("history")}
            className={`tab-btn ${currentPage === "history" ? "active" : ""}`}>
            License History
          </button>
        </div>
      </div>

      {currentPage === "licenses" && (
        <>
          <div className="dashboard-stats">
            {[
              { label: "Total Licenses", value: stats.total, color: "stat-total" },
              { label: "Active Licenses", value: stats.active, color: "stat-active" },
              { label: "Pending Activation", value: stats.pending, color: "stat-pending" },
              { label: "Expired/Inactive", value: stats.expired, color: "stat-expired" },
            ].map((s, i) => (
              <div key={i} className="stat-card">
                <h3>{s.label}</h3>
                <div className={`stat-value ${s.color}`}>{s.value}</div>
              </div>
            ))}
          </div>

          <div className="dashboard-controls">
            <input
              type="text"
              placeholder="🔍 Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <div className="filter-group">
              {["all", "active", "pending", "expired", "inactive"].map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`filter-btn ${activeFilter === f ? "active" : ""}`}>
                  {f}
                </button>
              ))}
            </div>
            <button onClick={() => setShowForm(true)} className="new-license-btn">
              ➕ Generate New License
            </button>
          </div>

          <div className="licenses-table-card">
            <div className="table-header">
              <h3>📋 All Licenses</h3>
              {refreshing && (
                <span className="refresh-indicator">🔄 Auto-refreshing...</span>
              )}
              <button onClick={() => loadLicenses(true)} className="refresh-btn">
                🔄 Refresh
              </button>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading licenses...</p>
              </div>
            ) : filteredLicenses.length === 0 ? (
              <div className="empty-state">
                <div>📭</div>
                <p>No licenses found</p>
              </div>
            ) : (
              <div className="licenses-table-scroll">
              <table className="licenses-table">
                <colgroup>
                  <col className="col-client" />
                  <col className="col-clinic" />
                  <col className="col-phone" />
                  <col className="col-city" />
                  <col className="col-license-key" />
                  <col className="col-application" />
                  <col className="col-status" />
                  <col className="col-activated" />
                  <col className="col-expiry" />
                  <col className="col-device-info" />
                  <col className="col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Client / Org</th>
                    <th>System</th>
                    <th>Clinic</th>
                    <th>Phone</th>
                    <th>City</th>
                    <th>License Key</th>
                    <th className="header-app">Application</th>
                    <th className="header-status">Status</th>
                    <th>Device Status</th>
                    <th className="header-expiry">Expiry<br />Date</th>
                    <th className="header-device">Device<br />Info</th>
                    <th className="actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLicenses.map((row) => {
                    if (row._rowType === "organisation") {
                      const statusInfo = row.statusInfo;
                      const activatedCount = row.systems.filter((s) => s.device_id).length;
                      return (
                        <tr key={row._rowKey}>
                          <td className="type-cell">
                            <span className="type-badge type-badge-organisation">Organisation</span>
                            {Number(row.invalid_attempt_count) > 0 && (
                              <span className="invalid-attempt-badge" title="Blocked activation attempts">
                                ⚠ {row.invalid_attempt_count}
                              </span>
                            )}
                          </td>
                          <td className="client-cell">
                            <button
                              type="button"
                              className="customer-link-btn"
                              onClick={() => openCustomerView(row)}
                            >
                              {row.org_name || row.client_name}
                            </button>
                            <div className="client-email">{row.email}</div>
                            {row.created_at && (
                              <div className="client-created">
                                Created {new Date(row.created_at).toLocaleDateString()}
                              </div>
                            )}
                          </td>
                          <td className="clinic-cell">{row.systems_count} systems</td>
                          <td className="clinic-cell">{row.clinic_name || "-"}</td>
                          <td className="phone-cell">{row.phone || "-"}</td>
                          <td className="city-cell">{row.city || "-"}</td>
                          <td className="license-key-cell">
                            <span className="org-license-summary">Multiple</span>
                          </td>
                          <td className="application-cell">
                            <code className="license-key">{row.application_name || "-"}</code>
                          </td>
                          <td className="status-cell">
                            <span className={`license-status ${statusInfo.class}`}>
                              {statusInfo.text}
                            </span>
                          </td>
                          <td className="activated-cell">
                            <span className={activatedCount > 0 ? "yes-badge" : "no-badge"}>
                              {activatedCount}/{row.systems_count} activated
                            </span>
                          </td>
                          <td className="expiry-cell">—</td>
                          <td className="device-info-cell">—</td>
                          <td className="actions-cell">
                            <div className="action-btns">
                              <button
                                onClick={() => openCustomerView(row)}
                                className="btn-view"
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    const lic = row;
                    const statusInfo = getStatusInfo(lic);
                    return (
                      <tr key={lic._rowKey || lic.id}>
                        <td className="type-cell">
                          <span className={`type-badge type-badge-${lic.customer_type || "individual"}`}>
                            Individual
                          </span>
                          {Number(lic.invalid_attempt_count) > 0 && (
                            <span className="invalid-attempt-badge" title="Blocked activation attempts">
                              ⚠ {lic.invalid_attempt_count}
                            </span>
                          )}
                        </td>
                        <td className="client-cell">
                          <button type="button" className="customer-link-btn" onClick={() => openCustomerView(lic)}>
                            {lic.client_name}
                          </button>
                          <div className="client-email">{lic.email}</div>
                        </td>
                        <td className="clinic-cell">{lic.system_name || "-"}</td>
                        <td className="clinic-cell">{lic.clinic_name || "-"}</td>
                        <td className="phone-cell">{lic.phone || "-"}</td>
                        <td className="city-cell">{lic.city || "-"}</td>
                        <td className="license-key-cell">
                          <code className="license-key">{lic.license_key}</code>
                        </td>
                        <td className="application-cell">
                          <code className="license-key">{lic.application_name}</code>
                        </td>
                        <td className="status-cell">
                          <span className={`license-status ${statusInfo.class}`}>
                            {statusInfo.text}
                          </span>
                        </td>
                        <td className="activated-cell">
                          <span className={lic.device_id ? "yes-badge" : "no-badge"}>
                            {formatLicenseActivationStatus(lic)}
                          </span>
                        </td>
                        <td className="expiry-cell">
                          {formatLicenseExpiry(lic)}
                        </td>
                        <td className="device-info-cell" title={formatLicenseDeviceInfo(lic)}>
                          <span className="device-info-summary">{formatLicenseDeviceInfo(lic)}</span>
                        </td>
                        <td className="actions-cell">
                          <div className="action-btns">
                            <button
                              onClick={() => setSelectedLicense(lic)}
                              className="btn-view">
                              View
                            </button>
                            {lic.device_id && lic.status === "active" && (
                              <button
                                onClick={() => handleUnbind(lic.id)}
                                className="btn-unbind"
                                title="Removes device binding so the license can be activated on a new machine">
                                Transfer to New Machine
                              </button>
                            )}
                            {lic.status === "active" && (
                              <button
                                onClick={() => handleMarkInactive(lic.id)}
                                className="btn-inactive">
                                Inactive
                              </button>
                            )}
                            {lic.status === "expired" && (
                              <button
                                onClick={() => setShowExtendModal(lic)}
                                className="btn-extend">
                                Extend
                              </button>
                            )}
                            {lic.status === "inactive" && (
                              <button
                                onClick={() => handleUpdateStatus(lic.id, "active")}
                                className="btn-activate">
                                Activate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>

          {showForm && (
            <LicenseFormModal
              onSubmit={handleCreateLicense}
              onCancel={() => setShowForm(false)}
              showAlert={showAlert}
              applicationData={applicationData}
            />
          )}
          {selectedLicense && (
            <LicenseDetailsModal
              license={selectedLicense}
              onClose={() => setSelectedLicense(null)}
            />
          )}
          {showExtendModal && (
            <ExtendLicenseModal
              license={showExtendModal}
              onExtend={(d) => handleExtendLicense(showExtendModal.id, d)}
              onCancel={() => setShowExtendModal(null)}
              showAlert={showAlert}
            />
          )}
        </>
      )}

      {currentPage === "requests" && (
        <LicenseRequestsPage showAlert={showAlert} showConfirm={showConfirm} />
      )}

      {currentPage === "history" && (
        <>
          <div className="dashboard-controls">
            <input
              type="text"
              placeholder="🔍 Search history..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="history-table-card">
            <div className="table-header">
              <h3>📜 License History</h3>
              <button onClick={loadHistory} className="refresh-btn">
                🔄 Refresh
              </button>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading history...</p>
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="empty-state">
                <div>📭</div>
                <p>No history records found</p>
              </div>
            ) : (
              <div className="history-table-scroll">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>License Key</th>
                      <th>Client Name</th>
                      <th>Action</th>
                      <th className="header-device">Device ID</th>
                      <th className="header-expiry">Expiry<br />Date</th>
                      <th>Performed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.map((entry) => (
                      <tr key={entry.id}>
                        <td className="history-date-cell">
                          {new Date(entry.timestamp).toLocaleString()}
                        </td>
                        <td className="license-key-cell">
                          <code className="license-key">{entry.license_key || "-"}</code>
                        </td>
                        <td className="client-cell">
                          <div className="client-name">{entry.client_name || "-"}</div>
                        </td>
                        <td className="status-cell">
                          <span className={`history-action ${entry.action}`}>
                            {entry.action}
                          </span>
                        </td>
                        <td className="device-id-cell">
                          {entry.device_id ? (
                            <code className="device-id-code" title={entry.device_id}>
                              {entry.device_id}
                            </code>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="expiry-cell">
                          {entry.expires_at
                            ? new Date(entry.expires_at).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="performed-by-cell">{entry.performed_by || "System"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {modalMessage && (
        <MessageModal message={modalMessage} onClose={() => setModalMessage("")} />
      )}

      {customerView && (
        <CustomerDetailPage
          customerId={customerView.id}
          customerType={customerView.type}
          onClose={() => setCustomerView(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
